import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, mergeMap, of } from 'rxjs';
import { ICreateDeviceType } from 'src/app/shared/models/ICreateDeviceType';
import { IDeviceType } from 'src/app/shared/models/IDevice-type';
import { ResourcesService } from 'src/app/shared/services/resources.service';
import { OnlySpacesValidator } from 'src/app/shared/util/onlySpaces.validator';
import { DeviceTypeService } from '../shared/device-type.service';
import { DeviceTypeValidator } from '../shared/device-type.validator';

@Component({
  selector: 'app-add-device-type',
  templateUrl: './add-device-type.component.html',
  styleUrls: ['./add-device-type.component.css']
})
export class AddDeviceTypeComponent implements OnInit {
  resourceKeyValue: any;
  isCreate = false;
  isLoading = false;
  pageTitle: string;
  deviceTypes: IDeviceType[];
  deviceModels: IDeviceType[] = [];
  modelsRegex = [];
  deviceTypeForm = this.fb.group({
    id: ['-1', []],
    deviceTypeName: ['', [Validators.required, Validators.maxLength(200), OnlySpacesValidator.onlyWhiteSpace]],
    deviceTypeCode: ['', [Validators.required, Validators.maxLength(50), OnlySpacesValidator.onlyWhiteSpace]],
    models: this.fb.array([])
  });
  constructor(private fb: UntypedFormBuilder,
    private resourcesService: ResourcesService,
    public toastrService: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private deviceTypeService: DeviceTypeService) {
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.resourcesService.bsObject.subscribe(result => {
      result && (this.resourceKeyValue = result);
      this.populateDeviceTypeDataForm();
    });
  }
  get models() {
    return this.deviceTypeForm.get('models') as UntypedFormArray;
  }
  addModelControl() {
    this.createModelControl('', false);
  }
  deleteModelControl(index: number) {
    if (this.models.length == 1) {
      return false;
    } else {
      this.models.markAsDirty();
      var modelControl = this.models.controls[index];
      var mIndex = this.modelsRegex.findIndex(x => x === modelControl.value);
      this.modelsRegex.splice(mIndex, 1);
      this.models.removeAt(index);
      return true;
    }
  }
  createModelControl(modelValue: string, disabled: boolean) {
    this.models.push(new UntypedFormControl({ value: modelValue, disabled: disabled }, [Validators.required, Validators.maxLength(500), OnlySpacesValidator.onlyWhiteSpace, DeviceTypeValidator.ValidateDeviceTypeModelPattern(), DeviceTypeValidator.ValidateDeviceTypeModel(this.modelsRegex)]));
  }
  populateDeviceTypeDataForm(): void {
    this.deviceTypeService.getAllDeviceTypesWithModels().pipe(
      mergeMap((deviceTypes: IDeviceType[]) => {
        if (deviceTypes && deviceTypes.length > 0) {
          this.deviceTypes = [...new Map(deviceTypes.map(item => [item['name'], item])).values()];
          this.modelsRegex = deviceTypes.map(x => x.regex);
        }
        return this.route.params;
      }),
      catchError(error => {
        console.log(error);
        return of([]);
      }),
      mergeMap((params: any) => {
        if (params['id']) {
          this.isCreate = false;
          const deviceTypeId = parseInt(params['id'], 10);
          return this.deviceTypeService.getDeviceTypeWithModelsByTypeId(deviceTypeId);
        } else {
          this.isCreate = true;
          this.models.clear();
          this.createModelControl('', false);
          return of([]);
        }
      }),
      catchError(error => {
        console.log(error);
        return of([]);
      })).subscribe((response: IDeviceType[]) => {
        if (response && response.length > 0) {
          this.models.clear();
          this.deviceModels = response;
          const deviceType = response[0];
          this.deviceTypeForm.patchValue({
            id: deviceType?.id,
            deviceTypeName: deviceType?.name,
            deviceTypeCode: deviceType?.code
          });
          response.forEach(x => {
            this.createModelControl(x.regex, true);
          });
        }
        this.setControlText();
        this.setValidators();
        this.isLoading = false;
      }), catchError(error => {
        console.log(error);
        this.isLoading = false;
        return of([]);
      });
  }

  onSubmit() {
    this.isLoading = true;
    var deviceTypeRequest = this.createRequestObject(this.deviceTypeForm);
    this.deviceTypeService.createDeviceType(deviceTypeRequest).subscribe(response => {
      if (response) {
        this.router.navigate(['/devicetypes']);
        this.isLoading = false;
        if (this.isCreate) {
          this.showSuccessNotification(this.resourceKeyValue.deviceTypeSuccessMessageOnCreate);
        } else {
          this.showSuccessNotification(this.resourceKeyValue.deviceTypeSuccessMessageOnUpdate);
        }
      } else {
        throw new Error('Failed to create device type');
      }
    }, (error: HttpErrorResponse) => {
      this.isLoading = false;
      //TODO: Add calling AI here.
      console.error(error);
      if (this.isCreate) {
        this.showErrorNotification(this.resourceKeyValue.deviceTypeErrorMessageOnCreate);
      } else {
        this.showErrorNotification(this.resourceKeyValue.deviceTypeErrorMessageOnUpdate);
      }
    });
  }
  createRequestObject(formGroup: UntypedFormGroup): ICreateDeviceType {
    let requestObj = {
      id: this.getTrimValue(formGroup, 'id'),
      deviceTypeName: formGroup.get('deviceTypeName')!.value,
      deviceTypeCode: formGroup.get('deviceTypeCode')!.value,
      regexList: this.getModelRegexList(),
      regexIdsToDelete: this.getRegexIdsToDelete()
    }
    return requestObj;
  }
  getModelRegexList(): string[] {
    const modelRegexList = [];
    (this.models.controls as UntypedFormControl[]).forEach(x => {
      if (this.deviceModels && this.deviceModels.length > 0) {
        var modelNotExists = this.deviceModels.find(y => y.regex === x.value);
        if (!modelNotExists)
          modelRegexList.push(x.value);
      } else {
        modelRegexList.push(x.value);
      }
    });
    return modelRegexList;
  }
  getRegexIdsToDelete(): any {
    const regexIdsToDelete = [];
    var modelControls = (this.models.controls as UntypedFormControl[]);
    if (this.deviceModels && modelControls) {
      this.deviceModels.forEach(x => {
        if (x.activeDeviceCount === 0) {
          var modelExists = modelControls.find(y => y.value === x.regex);
          if (!modelExists) {
            regexIdsToDelete.push(x.deviceModelId);
          }
        }
      });
    }
    return regexIdsToDelete;
  }
  getTrimValue(formGroup: UntypedFormGroup, fieldName: string) {
    const val = formGroup.get(fieldName).value;
    if (typeof val === "number") {
      return val;
    }
    else if (val !== undefined && val !== null) {
      return val.trim();
    }
    return "";
  }
  showSuccessNotification(message: string) {
    this.toastrService.success(message, this.resourceKeyValue.resourceKeyDeviceType, {
      timeOut: 3000,
      closeButton: true,
      positionClass: 'toast-top-center',
    });
  }

  showErrorNotification(message: string) {
    this.toastrService.error(message, this.resourceKeyValue.resourceKeyDeviceType, {
      timeOut: 3000,
      closeButton: true,
      positionClass: 'toast-top-center',
    });
  }

  setValidators(): void {
    this.deviceTypeForm.controls.deviceTypeName.setValidators([
      Validators.required,
      Validators.maxLength(200),
      OnlySpacesValidator.onlyWhiteSpace,
      DeviceTypeValidator.ValidateDeviceTypeName(this.deviceTypes)
    ]);
    this.deviceTypeForm.controls.deviceTypeCode.setValidators([
      Validators.required,
      Validators.maxLength(50),
      OnlySpacesValidator.onlyWhiteSpace,
      DeviceTypeValidator.ValidateDeviceTypeCode(this.deviceTypes)
    ]);
  }
  setControlText() {
    this.pageTitle = this.isCreate ? this.resourceKeyValue.createDeviceType : this.resourceKeyValue.editDeviceType;
  }
  setDeleteButtonVisibilty(regex: string, disabled: boolean): boolean {
    return disabled ? this.deviceModels.some(x => x.regex === regex && x.activeDeviceCount === 0) : true;
  }
}